import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useApprovalLevel } from "../../contexts/ApprovalLevelContext";
import Modal from "../../components/UI/Modal";
import ApprovalLevelForm from "../../components/Form/ApprovalLevelForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import Skeleton from "react-loading-skeleton";
import { MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid, Search, X } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";
import StatusBadge from '../../components/UI/StatusBadge';

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteApprovalLevel }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}>
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteApprovalLevel(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const ApprovalLevelMaster = React.memo(() => {
  const { approvalLevels, addApprovalLevel, updateApprovalLevel, deleteApprovalLevel, isLoading } = useApprovalLevel();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingApprovalLevel, setEditingApprovalLevel] = useState(null);
  const [message, setMessage] = useState(""); 
  const [formErrors, setFormErrors] = useState({}); 
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    setIsClient(true); 
  }, []);

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingApprovalLevel(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((approvalLevel) => {
    setEditingApprovalLevel(approvalLevel);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingApprovalLevel(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (approvalLevel) => {
      setLoading(true);
      try {
        if (editingApprovalLevel) {
          const approvalLevelData = approvalLevel instanceof FormData ? convertFormDataToObject(approvalLevel) : approvalLevel;
          await updateApprovalLevel({ ...editingApprovalLevel, ...approvalLevelData });
          setNotification({
            message: "Approval Level updated successfully.",
            type: "success",
          });
        } else {
          await addApprovalLevel(approvalLevel);
          setNotification({
            message: "Approval Level added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        const apiErrors = err.response?.data?.errors || {};
        const apiMessage = err.response?.data?.message || err.response?.data?.error || "An error occurred";
        
        if (Object.keys(apiErrors).length > 0) {
          setFormErrors(apiErrors);
        } else {
          setNotification({
            message: apiMessage,
            type: "error",
          });
        }
      } finally {
        setLoading(false); 
      }
    },
    [editingApprovalLevel, addApprovalLevel, updateApprovalLevel, closeModal]
  );

  // Memoize candidates with campaign names for the table
  const approvalLevelForTable = useMemo(
    () =>
      approvalLevels.map((approvalLevel) => ({
        ...approvalLevel,
        name: approvalLevel?.name || "Unknown Approver Level",
      })),
    [approvalLevels]
  );

  const filteredApprovalLevels = useMemo(() => {
    return approvalLevelForTable.filter((level) => {
      const searchTerm = searchQuery.toLowerCase();
      const matchesSearch = 
        (level.name?.toLowerCase() || "").includes(searchTerm) ||
        (String(level.sequence)?.toLowerCase() || "").includes(searchTerm);
      
      const matchesStatus = 
        statusFilter === "" || 
        String(level.is_active) === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [approvalLevelForTable, searchQuery, statusFilter]);

  const handleDeleteApprovalLevel = async (id) => {
    try {
      await deleteApprovalLevel(id);
      // setNotification("Approver module deleted successfully");
      setNotification({
        message: "Approval level deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete approver module");
      setNotification({
        message: "Failed to delete approval level",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      { label: "Stage", field: "sequence" },
      { label: "Status", field: "is_active",
        render: (row) => (
         <StatusBadge status={(row?.is_active === 1 || row?.is_active === "1") ? "active" : "inactive"} />
        ),
       },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteApprovalLevel={handleDeleteApprovalLevel}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteApprovalLevel,
    ]
  );

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Approval Level Masters</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl"> 
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or sequence..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Filter and Add Button */}
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="w-full sm:w-48 relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none cursor-pointer font-poppins"
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
          >
            Add Approval Level
          </button>
        </div>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Skeleton Loader */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      ) : (
        <div>
          <ReusableTable
            data={filteredApprovalLevels}
            // data={requests.slice().reverse()}
            loading={isLoading}
            columns={columns}
            filterable={false}
            excludeFilterColumns={["actions"]}
            // excludeFilterColumns={excludeFilterColumns}
          />
        </div>
      )}

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingApprovalLevel ? "Edit Approval Level" : "Add Approval Level"}
      >
        <ApprovalLevelForm
          loading={loading} 
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingApprovalLevel || { name: "", sequence: "", is_active: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default ApprovalLevelMaster;
